import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
// Service
import { SystemApiService } from '@cv-invision/core';
// Model
import { INTEGRATIONS } from '../../model/global-configuration.constant';
import { ProductsModel } from '../model/product.model';
import { IdTitleObjectType } from '../../state/models/global-configuration-state.model';

@Injectable({
	providedIn: 'root'
})
export class ProductsService {
	private setReloadlListConfirmation = new BehaviorSubject({});
	private savingProductSubject = new BehaviorSubject({});
	private getReloadlListListener = this.setReloadlListConfirmation.asObservable();
	private savingProductListener = this.savingProductSubject.asObservable();
	constructor(private httpClient: HttpClient, private systemApiService: SystemApiService) { }
	/**
	 * GET - Return list of products.
	 */
	getProducts(filter: any): Observable<any> {
		let httpParams: any = new HttpParams();
		const { pagination, title, origin, active } = filter;
		if (title) {
			httpParams = httpParams.append('title', title);
		}

		if (origin) {
			httpParams = httpParams.append('origin', origin);
		}

		if (active !== null && active !== undefined) {
			httpParams = httpParams.append('active', active);
		}

		if (pagination) {
			const { limit, offset } = pagination;
			httpParams = httpParams.append('limit', limit.toString());
			httpParams = httpParams.append('offset', offset.toString());
		}
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products`, {
			params: httpParams
		});
	}
	/**
	 * GET - Returns list of schemas.
	 */
	getSchemas(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.docsys }/schemas`).pipe(
			map((response: any) => {
				const sch = (response || []).filter(s => s.schema.includes('InspectionReport') || s.schema.includes('Inspectionless'));
				return sch.map(schema => {
					const version = `${ schema.version }`.includes('.') ? schema.version : `${ schema.version }.0`;
					return {
						id: schema.id,
						name: `${ schema.schema }:${ version }`
					};
				});
			})
		);
	}

	/**
	 * get product list by integration
	 */
	getProductListByIntegretion(product: INTEGRATIONS): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/${ product }`);
	}

	/**
	 * get Nexsys list attributes
	 */
	getNexsysListAttributes(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/nexsys-attributes`);
	}

	/**
	 * get product list by integration
	 */
	getProductListByOrigin(source: string): Observable<any> {
		let httpParams: any = new HttpParams();
		httpParams = {
			Origin: [ source ],
			OnlySubProducts: true,
			limit: 0
		};
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products`, {
			params: httpParams
		});
	}

	/**
	 * get product  by ID
	 */
	getProductListById(id: string): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/${ id }`);
	}

	/**
	 * save a new product
	 */
	setNewProduct(params: ProductsModel): Observable<any> {
		return this.httpClient.post(`${ this.systemApiService.api.orders }/products`, params);
	}

	/**
	 * update a new product
	 */
	updateProduct(id: string, params: ProductsModel): Observable<any> {
		return this.httpClient.put(`${ this.systemApiService.api.orders }/products/${ id }`, params);
	}

	/**
	 * get AssetsOnCompletion by origin
	 */
	getAssetsOnCompletion(origin: string): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/product-assets-list/${ origin }`);
	}

	/**
	 * get Milestone list for acuity
	 */
	getMilestoneList(origin: string): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/utils/${ origin }/milestones`);
	}

	/**
	 * get Milestone list for Cmp acuity
	 */
	getCmpMilestoneList(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/utils/cmpacuity/milestones`);
	}

	getTrackingSystems(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/utils/tracking-systems`);
	}

	getHandleMode(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/utils/enums/handleMode`);
	}

	getOrigins(): Observable<any> {
		return this.httpClient.get(`${this.systemApiService.api.orders}/utils/origins`);
	}

	/**
	 * Sets component data communication.
	 * @param data
	 */
	setReloadListConfirmation(data: any): void {
		this.setReloadlListConfirmation.next(data);
	}

	/**
	 * Returns si the list should be reloaded.
	 */
	getReloadListConfirmationListener(): Observable<any> {
		return this.getReloadlListListener;
	}

	/**
	 * Sets Product Add/Edit Header <-> Product Add/Edit component data communication.
	 * @param data
	 */
	setSavingProductData(data: any): void {
		this.savingProductSubject.next(data);
	}

	/**
	 * Returns Saving Product listener observable.
	 */
	getSavingProductListener(): Observable<any> {
		return this.savingProductListener;
	}

	/**
	 * Returns software collector options as an observable.
	 */
	getOptionsSoftwareColector(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/software-collector`);
	}

	/**
	 * clone a product
	 */
	cloneProduct(id: string): Observable<any> {
		let httpParams: any = new HttpParams();
		httpParams = httpParams.append('productSourceId', id);
		return this.httpClient.post(`${ this.systemApiService.api.orders }/products/clone-product?productSourceId=${ id }`, HttpParams);
	}

	/**
	 * GET - Returns list of Product Types.
	 */
	getProductTypes(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/product-types`).pipe(
			map((response: any) => {
				if (response && response.length > 0) {
					return response.map(type => {
						return {
							id: type,
							name: type
						};
					});
				} else {
					return [];
				}
			})
		);
	}

	/**
	 * GET - Returns list of Inspection Types.
	 */
	getInspectionTypes(): Observable<any> {
		return this.httpClient.get(`${ this.systemApiService.api.orders }/products/inspection-types`).pipe(
			map((response: any) => {
				if (response && response.length > 0) {
					return response.map(type => {
						return {
							id: type,
							name: type
						};
					});
				} else {
					return [];
				}
			})
		);
	}

	/**
	 * GET - Returns list of UAD36 Field Types.
	 */
	getUAD36FieldTypes(): Observable<any> {
		return this.httpClient.get(`${this.systemApiService.api.orders}/utils/enums/UAD36Fields`).pipe(
			map((response: any) => {
				if (response && response.length > 0) {
					return response.map((item: IdTitleObjectType) => {
						return {
							id: item.id,
							name: item.title
						};
					});
				} else {
					return [];
				}
			})
		);
	}
}
